import { useHandler } from "@redotech/react-util/hook";
import {
  TimeUnit,
  type ConversationStatusV3,
} from "@redotech/redo-flows-common/rules-v3/types";
import type { MergeSupportTicketAction as MergeSupportTicketActionType } from "@redotech/redo-flows-common/rules/schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import {
  supportTicketStatusItems,
  supportTicketStatusLabels,
} from "../support-ticket-status-items";

const unitLabels = (): Record<TimeUnit, string> => ({
  [TimeUnit.MINUTES]: t("Minutes", { context: "time unit" }),
  [TimeUnit.HOURS]: t("Hours", { context: "time unit" }),
  [TimeUnit.DAYS]: t("Days", { context: "time unit" }),
});

const unitOptions = (): RedoListItem<TimeUnit>[] =>
  [TimeUnit.MINUTES, TimeUnit.HOURS, TimeUnit.DAYS].map((unit) => ({
    id: unit,
    type: "text" as const,
    text: unitLabels()[unit],
    value: unit,
  }));

export const MergeSupportTicketAction = memo(function MergeSupportTicketAction({
  action,
  onChange,
  editableFields,
}: {
  action: MergeSupportTicketActionType;
  onChange: (updated: MergeSupportTicketActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const allowedStatusesEditable =
    editableFields === undefined || editableFields.has("allowedStatuses");
  const ageEditable =
    editableFields === undefined ||
    editableFields.has("maxAgeSinceLastMessage");

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState<HTMLButtonElement | null>(
    null,
  );

  const selectedStatusItems = useMemo(
    () =>
      supportTicketStatusItems().filter((opt) =>
        action.allowedStatuses.includes(opt.value),
      ),
    [action.allowedStatuses],
  );

  const statusButtonText = useMemo(() => {
    if (action.allowedStatuses.length === 0) {
      return t("Select statuses");
    }
    return action.allowedStatuses
      .map((s) => supportTicketStatusLabels()[s])
      .join(", ");
  }, [action.allowedStatuses, t]);

  const handleStatusesChange = useHandler(
    (items: RedoListItem<ConversationStatusV3>[]) => {
      if (!allowedStatusesEditable) {
        return;
      }
      onChange({ ...action, allowedStatuses: items.map((item) => item.value) });
    },
  );

  const selectedUnit = useMemo(
    () =>
      unitOptions().find(
        (opt) => opt.value === action.maxAgeSinceLastMessage.unit,
      ),
    [action.maxAgeSinceLastMessage.unit],
  );

  const handleValueChange = useHandler((value: string) => {
    if (!ageEditable) {
      return;
    }
    const parsed = parseInt(value, 10);
    onChange({
      ...action,
      maxAgeSinceLastMessage: {
        ...action.maxAgeSinceLastMessage,
        value: isNaN(parsed) || parsed < 1 ? 1 : parsed,
      },
    });
  });

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex dir="column" gap="xs" w="full">
        <Text fontSize="sm" fontWeight="medium">
          {t("Merge tickets in these statuses")}
        </Text>
        <RedoButton
          disabled={!allowedStatusesEditable}
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
          ref={setStatusAnchor}
          size="sm"
          text={statusButtonText}
        />
        <RedoMultiselectDropdown<ConversationStatusV3>
          dropdownAnchor={statusAnchor}
          dropdownOpen={statusDropdownOpen && allowedStatusesEditable}
          fitToAnchor
          items={supportTicketStatusItems()}
          requireNItems={1}
          selectedItems={selectedStatusItems}
          setDropdownOpen={setStatusDropdownOpen}
          setSelectedItems={handleStatusesChange}
          size="sm"
        />
      </Flex>
      <Flex dir="column" gap="xs" w="full">
        <Text fontSize="sm" fontWeight="medium">
          {t("Only merge tickets active within")}
        </Text>
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={1}
            placeholder="0"
            setValue={handleValueChange}
            size="sm"
            value={action.maxAgeSinceLastMessage.value.toString()}
          />
          <RedoSingleSelectDropdownInput<TimeUnit>
            disabled={!ageEditable}
            fitToAnchor
            options={unitOptions()}
            optionSelected={({ item }) => {
              if (!ageEditable) {
                return;
              }
              onChange({
                ...action,
                maxAgeSinceLastMessage: {
                  ...action.maxAgeSinceLastMessage,
                  unit: item.value,
                },
              });
            }}
            selectedItem={selectedUnit}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
